import { useContext, useState, useCallback } from "react"
import {
  post_middleware_osrm_search,
  get_module_entity_action_middleware,
  post_module_entity_action_middleware,
} from "services/middleware_services"
import useCancelToken from "hooks/useCancelToken"
import { AppContext } from "store/context/AppContext"
import AuthContext from "store/context/PublicContext"
import useAuthorizer from "overrides/theme/entel/base/hooks/useAuthorizer"
import Swal from "sweetalert2"
import OMNIX_MODULE from "constants/OMNIX_MODULE"

const useSearchMiddlewareOSRM = ({ entity, silent404 }: any) => {
  const [data, set_data] = useState<any>()
  const { newCancelToken, isCancel } = useCancelToken()
  const { logout_user_action } = useAuthorizer()

  const {
    user: { token },
  } = useContext(AuthContext)

  const { loading_app, set_loading_app } = useContext(AppContext)

  const search_osrm_action = useCallback(
    async (body: any) => {
      try {
        set_loading_app(true)
        set_data(null)
        let {
          data: { message },
        } = await post_middleware_osrm_search(entity, body, token, newCancelToken())
        set_data(message)
        set_loading_app(false)
      } catch (error: any) {
        set_loading_app(false)
        if (isCancel(error)) return
        if (error?.response?.status === 404) {
          if (!silent404) {
            Swal.fire({
              html: `
                <div class="flex flex-col items-center justify-center pt-4 pb-1">
                  <div class="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                    <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                  </div>
                  <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">Sin resultados</h3>
                  <p class="text-[13px] text-gray-500 font-medium leading-snug px-2">No se encontraron resultados para esta búsqueda.</p>
                </div>
              `,
              showConfirmButton: true,
              confirmButtonText: "Aceptar",
              buttonsStyling: false,
              customClass: {
                popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
                actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
                confirmButton: "!w-full !bg-transparent !text-[#007AFF] !text-[16px] !font-semibold !py-3 m-0 hover:!bg-gray-50 transition-colors !outline-none focus:!outline-none focus:!ring-0 !shadow-none"
              }
            })
          } else {
            set_data({ _isError: true, _status: 404, _detail: error?.response?.data?.message?.detail })
          }
        } else {
          Swal.fire({
            html: `
              <div class="flex flex-col items-center justify-center pt-4 pb-1">
                <div class="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                  <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                  </svg>
                </div>
                <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">Error</h3>
                <p class="text-[13px] text-gray-500 font-medium leading-snug px-2 text-center">${error?.response?.data?.message || 'Ocurrió un error inesperado.'}</p>
              </div>
            `,
            showConfirmButton: true,
            confirmButtonText: "Aceptar",
            buttonsStyling: false,
            customClass: {
              popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
              actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
              confirmButton: "!w-full !bg-transparent !text-[#F9004D] !text-[16px] !font-semibold !py-3 m-0 hover:!bg-gray-50 transition-colors !outline-none focus:!outline-none focus:!ring-0 !shadow-none"
            }
          })
        }
        if ([401].includes(error?.response?.status)) return logout_user_action()
      }
    },
    [entity, token, newCancelToken, set_loading_app, isCancel, logout_user_action, silent404],
  )

  const action_search_osrm_action = useCallback(
    async (_entity: string, _body: any) => {
      try {
        const res = await post_middleware_osrm_search(_entity, _body, token, newCancelToken())
        return res
      } catch (error: any) {
        if (isCancel(error)) return
        if (error?.response?.status === 404) {
          if (!silent404) {
            Swal.fire({
            html: `
              <div class="flex flex-col items-center justify-center pt-4 pb-1">
                <div class="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                  <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                </div>
                <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">Sin resultados</h3>
                <p class="text-[13px] text-gray-500 font-medium leading-snug px-2">No se encontraron resultados para esta búsqueda.</p>
              </div>
            `,
            showConfirmButton: true,
            confirmButtonText: "Aceptar",
            buttonsStyling: false,
            customClass: {
              popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
              actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
              confirmButton: "!w-full !bg-transparent !text-[#007AFF] !text-[16px] !font-semibold !py-3 m-0 hover:!bg-gray-50 transition-colors !outline-none focus:!outline-none focus:!ring-0 !shadow-none"
            }
          })
        }
      } else {
        Swal.fire({
          html: `
            <div class="flex flex-col items-center justify-center pt-4 pb-1">
              <div class="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">Error</h3>
              <p class="text-[13px] text-gray-500 font-medium leading-snug px-2 text-center">${error?.response?.data?.message || 'Ocurrió un error inesperado.'}</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: "Aceptar",
          buttonsStyling: false,
          customClass: {
            popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
            actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
            confirmButton: "!w-full !bg-transparent !text-[#F9004D] !text-[16px] !font-semibold !py-3 m-0 hover:!bg-gray-50 transition-colors !outline-none focus:!outline-none focus:!ring-0 !shadow-none"
          }
        })
      }
        if ([401].includes(error?.response?.status)) return logout_user_action()
      }
    },
    [token, newCancelToken, isCancel, logout_user_action, silent404],
  )

  const action_get_reports_osrm_action = useCallback(
    async (_endpoint: string, _body: any) => {
      try {
        const res = await get_module_entity_action_middleware(
          OMNIX_MODULE.OSRM,
          _endpoint,
          _body,
          token,
          newCancelToken(),
        )
        return res
      } catch (error: any) {
        if (isCancel(error)) return
        Swal.fire({
          html: `
            <div class="flex flex-col items-center justify-center pt-4 pb-1">
              <div class="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">Error</h3>
              <p class="text-[13px] text-gray-500 font-medium leading-snug px-2 text-center">${error?.response?.data?.message || 'Ocurrió un error inesperado.'}</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: "Aceptar",
          buttonsStyling: false,
          customClass: {
            popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
            actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
            confirmButton: "!w-full !bg-transparent !text-[#F9004D] !text-[16px] !font-semibold !py-3 m-0 hover:!bg-gray-50 transition-colors !outline-none focus:!outline-none focus:!ring-0 !shadow-none"
          }
        })
        if ([401].includes(error?.response?.status)) return logout_user_action()
      }
    },
    [token, newCancelToken, isCancel, logout_user_action],
  )

  const action_post_reports_osrm_action = useCallback(
    async (_endpoint: string, _body: any) => {
      try {
        const res = await post_module_entity_action_middleware(
          OMNIX_MODULE.OSRM,
          _endpoint,
          _body,
          token,
          newCancelToken(),
        )
        return res
      } catch (error: any) {
        if (isCancel(error)) return
        Swal.fire({
          html: `
            <div class="flex flex-col items-center justify-center pt-4 pb-1">
              <div class="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              </div>
              <h3 class="text-[17px] font-semibold text-gray-900 tracking-tight mb-1">Error</h3>
              <p class="text-[13px] text-gray-500 font-medium leading-snug px-2 text-center">${error?.response?.data?.message || 'Ocurrió un error inesperado.'}</p>
            </div>
          `,
          showConfirmButton: true,
          confirmButtonText: "Aceptar",
          buttonsStyling: false,
          customClass: {
            popup: "!w-[280px] !rounded-[14px] !p-0 overflow-hidden shadow-2xl border border-gray-100",
            actions: "!mx-0 !mt-2 !w-full !border-t !border-gray-200/60 !p-0",
            confirmButton: "!w-full !bg-transparent !text-[#F9004D] !text-[16px] !font-semibold !py-3 m-0 hover:!bg-gray-50 transition-colors !outline-none focus:!outline-none focus:!ring-0 !shadow-none"
          }
        })
        if ([401].includes(error?.response?.status)) return logout_user_action()
      }
    },
    [token, newCancelToken, isCancel, logout_user_action],
  )

  return {
    data,
    set_data,
    loading_app,
    set_loading_app,
    search_osrm_action,
    action_search_osrm_action,
    action_get_reports_osrm_action,
    action_post_reports_osrm_action,
  }
}

export default useSearchMiddlewareOSRM
